import { useParams, Link, useNavigate } from 'react-router-dom'
import { useComposer, useUpdateComposer } from '@/hooks/useComposers'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { ConfidenceBadge } from '@/components/shared/ConfidenceBadge'
import { QualityDropdown } from '@/components/shared/QualityDropdown'
import { WikidataLink } from '@/components/shared/WikidataLink'
import { InlineEdit } from '@/components/shared/InlineEdit'
import { Button } from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { ArrowLeft } from 'lucide-react'
import type { QualityScore } from '@brent/shared/types'

export function ComposerDetail() {
  const { id } = useParams()
  const composerId = Number(id)
  const navigate = useNavigate()
  const { data, isLoading, error } = useComposer(composerId)
  const { canEdit } = useAuth()
  const updateComposer = useUpdateComposer()
  const { toast } = useToast()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading composer...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
        <h2 className="font-semibold text-destructive">Failed to load composer</h2>
        <p className="mt-1 text-sm text-muted-foreground">{error.message}</p>
      </div>
    )
  }

  if (!data) return null

  const { composer, works, concerts } = data

  const handleQualityChange = async (qualityScore: QualityScore) => {
    try {
      await updateComposer.mutateAsync({
        id: composer.id,
        input: { quality_score: qualityScore },
      })
      toast({ title: 'Updated', description: `Quality set to ${qualityScore}` })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update quality',
      })
    }
  }

  const handleQidSave = async (qid: string) => {
    try {
      const result = await updateComposer.mutateAsync({
        id: composer.id,
        input: { wikidata_id: qid },
      })
      if (result.enriched) {
        toast({
          title: 'Enriched',
          description: `Matched to ${result.enriched.label}`,
        })
      } else if (result.cleared) {
        toast({ title: 'Cleared', description: 'Wikidata ID removed' })
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update QID',
      })
    }
  }

  const dates =
    composer.birth_year || composer.death_year
      ? `${composer.birth_year || '?'}\u2013${composer.death_year || '?'}`
      : null

  return (
    <div className="space-y-8">
      <div>
        <Link to="/composers">
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to composers
          </Button>
        </Link>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{composer.name_canonical}</h1>
            {dates && <p className="mt-1 text-muted-foreground">{dates}</p>}
          </div>
          <div className="flex items-center gap-3">
            <ConfidenceBadge
              confidence={composer.confidence}
              qualityScore={composer.quality_score}
            />
            {canEdit ? (
              <QualityDropdown
                value={composer.quality_score}
                onChange={handleQualityChange}
                disabled={updateComposer.isPending}
              />
            ) : (
              <Badge variant="secondary" className="capitalize">
                {composer.quality_score.replace('_', ' ')}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Metadata */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Details</h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Wikidata ID</dt>
            <dd className="mt-1">
              {canEdit ? (
                <InlineEdit
                  value={composer.wikidata_id || ''}
                  onSave={handleQidSave}
                  placeholder="Add QID (e.g. Q1339)"
                />
              ) : (
                <WikidataLink qid={composer.wikidata_id} />
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Wikidata Label</dt>
            <dd className="mt-1 text-sm">{composer.wikidata_label || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Programme Appearances</dt>
            <dd className="mt-1 text-sm tabular-nums">{composer.programme_count}</dd>
          </div>
          {dates && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Dates</dt>
              <dd className="mt-1 text-sm">{dates}</dd>
            </div>
          )}
        </dl>
      </section>

      {/* Works */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">
          Works <span className="text-muted-foreground font-normal">({works.length})</span>
        </h2>
        {works.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>QID</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead className="text-right">Performances</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {works.map((work) => (
                  <TableRow
                    key={work.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/works/${work.work_id}`)}
                  >
                    <TableCell className="font-medium">
                      {work.title_canonical}
                      {work.title_short && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({work.title_short})
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <WikidataLink qid={work.wikidata_id} />
                    </TableCell>
                    <TableCell>
                      <ConfidenceBadge
                        confidence={work.confidence}
                        qualityScore={work.quality_score}
                      />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {work.programme_count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No works linked to this composer.</p>
        )}
      </section>

      {/* Concert appearances */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">
          Concert Appearances{' '}
          <span className="text-muted-foreground font-normal">({concerts.length})</span>
        </h2>
        {concerts.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Venue</TableHead>
                  <TableHead>Work</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {concerts.map((item, idx) => (
                  <TableRow
                    key={`${item.concert_id}-${item.position}-${idx}`}
                    className="cursor-pointer"
                    onClick={() => navigate(`/concerts/${item.concert_id}`)}
                  >
                    <TableCell className="whitespace-nowrap">
                      {item.date_text || `Concert #${item.concert_id}`}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {item.venue_name || '-'}
                    </TableCell>
                    <TableCell>
                      {item.work_title || item.work_text || (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {item.arrangement_note || item.notes || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No concert appearances recorded.</p>
        )}
      </section>
    </div>
  )
}
